import { createBatchEventProcessor, createInstance, createPollingProjectConfigManager } from '@optimizely/optimizely-sdk';

let optimizelyClientInstance: ReturnType<typeof createInstance> | null = null;
let pollingConfigManager: ReturnType<typeof createPollingProjectConfigManager> | null = null;
let isReady = false;
const optimizelyTimeout = 3000;

function forcedVariationToEnabled(variationKey: string): boolean {
  const normalized = variationKey.trim().toLowerCase();
  return !['off', 'false', '0', 'control', 'disabled'].includes(normalized);
}

/**
 * Hook object for devtools to register a forced decision provider.
 * Stays empty in production
 */
export const _devHooks: {
  getForcedDecision?: (experimentKey: string, userId: string) => string | undefined;
} = {};

export function getOptimizelyInstance(sdkKey: string) {
  if (!optimizelyClientInstance && sdkKey) {
    pollingConfigManager = createPollingProjectConfigManager({
      sdkKey,
      autoUpdate: true,
      updateInterval: 300000,
    });

    const batchEventProcessor = createBatchEventProcessor();

    optimizelyClientInstance = createInstance({
      projectConfigManager: pollingConfigManager,
      eventProcessor: batchEventProcessor,
    });

    optimizelyClientInstance
      .onReady()
      .then(() => {
        isReady = true;
      })
      .catch((err) => console.error('Error initializing Optimizely client:', err));
  }

  return optimizelyClientInstance;
}

export const optimizelyInstance = getOptimizelyInstance(import.meta.env['VITE_OPTIMIZELY_KEY'] || '');

export type Analytics = {
  track: (event: string, properties?: Record<string, any>) => void;
  getUserId: () => string;
};

async function getDecision(
  featureKey: string,
  userId: string,
  attributes: Record<string, any> = {},
) {
  if (!optimizelyInstance) return null;
  if (!isReady) {
    await optimizelyInstance.onReady({ timeout: optimizelyTimeout });
    isReady = true;
  }

  const enrichedAttributes = { ...attributes, Service: 'monorepo' };
  const user = optimizelyInstance.createUserContext(userId, enrichedAttributes);
  if (!user) return null;

  return user.decide(featureKey);
}

export async function decide(
  experimentKey: string,
  attributes: Record<string, any> = {},
  analytics: Analytics,
): Promise<string | null> {
  try {
    const userId = analytics.getUserId();
    const forced = _devHooks.getForcedDecision?.(experimentKey, userId);
    if (forced) return forced;

    const decision = await getDecision(experimentKey, userId, attributes);
    if (!decision) return null;

    analytics.track('OptimizelyDecision', {
      experiment: experimentKey,
      variation: decision.variationKey || 'control',
    });

    return decision.variationKey;
  } catch {
    return null;
  }
}

export async function decideEnabled(
  featureKey: string,
  attributes: Record<string, any> = {},
  analytics: Analytics,
): Promise<boolean> {
  try {
    const userId = analytics.getUserId();
    const forced = _devHooks.getForcedDecision?.(featureKey, userId);
    if (forced) return forcedVariationToEnabled(forced);

    const decision = await getDecision(featureKey, userId, attributes);
    if (!decision) return false;

    return decision.enabled ?? false;
  } catch {
    return false;
  }
}